import { compact, groupBy, maxBy } from 'lodash';
import { DateTime } from 'luxon';

import type { CampaignKeywordAnnotation } from 'src/data/annotation';
import type { Brief } from 'src/data/brief';
import { BRIEF_TYPE } from 'src/data/brief/const';
import {
  GENERATION_GUIDANCE_TYPE,
  GENERATION_SOURCE_TYPE,
  type GenerationRequest,
  getGenerationRequestTool,
  isSuggestGenerationResult,
} from 'src/data/generation';
import {
  actualizeGenerationTool,
  GENERATION_TOOL,
  isFreestyleGenerationTool,
  isUsedTemplate,
} from 'src/data/generationTool';
import { isRecomposeVariation, isSuggestionVariation } from 'src/data/variation';
import type { ID } from 'src/lib/collection';
import { formatDateLocale, parseDateTime } from 'src/lib/format';
import { unwrapEdges } from 'src/lib/graphql';

import {
  ACCESS_STATE_TYPE,
  ASSET_GROUP_TYPE,
  type AssetGroupType,
  BAREBONE_PROJECT_ID,
  DEFAULT_BLOG_NAME,
  DEFAULT_DOCUMENT_NAME,
  PROJECT_STATUS,
  type ProjectType,
} from './const';
import { getBriefTypeByProjectType } from './projectBriefTypesAssociation';
import { isWtmProjectType } from './projectType';
import type {
  FeedAsset,
  FeedAssetGroup,
  LongFormFeedAsset,
  LongFormFeedGroup,
  Project,
  ShortFormFeedAsset,
  ShortFormFeedGroup,
} from './types';

/**
 * Project grouping and sorting
 */
const DATE_GROUPS = {
  today: { index: 1, name: 'Modified today' },
  yesterday: { index: 2, name: 'Modified yesterday' },
  last30Days: { index: 3, name: 'Modified in the last 30 days' },
  sometime: { index: 4, name: 'Modified some time ago' },
};

const generateDateGroup = (project: Project): string => {
  const editedDate = parseDateTime(project.editedAt);
  if (editedDate && editedDate.isValid) {
    const today = DateTime.local();
    const yesterday = today.minus({ days: 1 });
    const ago30days = today.minus({ days: 30 });

    if (editedDate.hasSame(today, 'day')) {
      return DATE_GROUPS.today.name;
    }
    if (editedDate.hasSame(yesterday, 'day')) {
      return DATE_GROUPS.yesterday.name;
    }
    if (editedDate.hasSame(ago30days, 'day') || editedDate >= ago30days) {
      return DATE_GROUPS.last30Days.name;
    }
  }

  return DATE_GROUPS.sometime.name;
};

export const sortProjects = (projects: Project[]): Project[] =>
  [...projects].sort((a, b) => {
    const aDate = parseDateTime(a.editedAt);
    const bDate = parseDateTime(b.editedAt);
    return bDate.toMillis() - aDate.toMillis();
  });

export const groupProjects = (projects: Project[]): { name: string; projects: Project[] }[] => {
  // Sort 'projects' by 'updatedAt' DESC
  const sorted = sortProjects(projects);

  // Generate 'dateGroup' based on 'updatedAt' value
  const withDateGroups = sorted.map((project) => ({
    ...project,
    dateGroup: generateDateGroup(project),
  }));

  // Group by 'dateGroup'
  const groupedObj = groupBy(withDateGroups, 'dateGroup');

  // Prepare result
  return Object.entries(groupedObj).map(([group, groupedProjects]) => ({
    name: group,
    projects: groupedProjects,
  }));
};

export const getBareboneProject = (projectType: ProjectType): Project => {
  const briefType = getBriefTypeByProjectType(projectType);

  return {
    id: BAREBONE_PROJECT_ID,
    type: projectType,
    name: DEFAULT_DOCUMENT_NAME,
    brief: {
      type: briefType,
      lastModifiedAt: new Date().toISOString(),
    },
    editedAt: new Date().toISOString(),
  };
};

export const isBareboneProjectId = (projectId: ID): boolean => projectId === BAREBONE_PROJECT_ID;

export const isBareboneProject = (project: Project) => project && isBareboneProjectId(project.id);

export const generateProjectName = (brief: Brief): string => {
  switch (brief?.type) {
    case BRIEF_TYPE.canvas:
      return DEFAULT_DOCUMENT_NAME;

    case BRIEF_TYPE.blogPostBuilder:
      return DEFAULT_BLOG_NAME;

    default:
      return '';
  }
};

const prepareBrief = (brief: Brief): Brief => {
  const result = { ...brief };

  const { lastModifiedAt } = result;
  if (lastModifiedAt) {
    result.lastModifiedAt = formatDateLocale(lastModifiedAt);
  }

  return result;
};

const prepareCampaignKeywords = (
  generationRequests: GenerationRequest[]
): CampaignKeywordAnnotation[] => {
  const campaignKeywords = generationRequests[0]?.generationGuidance?.campaignKeywords || [];

  return campaignKeywords.map((keyword) => ({
    id: `${GENERATION_GUIDANCE_TYPE.campaignKeyword}${keyword}`,
    type: GENERATION_GUIDANCE_TYPE.campaignKeyword,
    content: keyword,
  }));
};

export const prepareLoadedProject = (project: Project) => {
  const result = { ...project };
  const { brief, customerPersonas, generationRequests } = result;

  if (brief) {
    result.brief = prepareBrief(brief);
  }
  if (customerPersonas) {
    result.customerPersonaIds = compact(customerPersonas.map((c) => c.id));
  }
  if (generationRequests) {
    const requests = unwrapEdges<GenerationRequest>(generationRequests.edges);
    result.annotations = prepareCampaignKeywords(requests);
  }

  return result;
};

export const unwrapProjects = (projectEdges: { node: Project }[]) => {
  const nodes = unwrapEdges<Project>(projectEdges);
  return nodes.map((node) => prepareLoadedProject(node));
};

export const isProjectReadOnly = (project: Project): boolean => {
  if (!project) {
    return false;
  }

  return (
    project.status === PROJECT_STATUS.legacy ||
    (project.accessState !== ACCESS_STATE_TYPE.read_write && !isWtmProjectType(project.type))
  );
};

export const getProjectLatestGenerationResult = (project: Project): GenerationRequest | undefined =>
  project?.generationRequests?.edges[0]?.node;

const getProjectNewestAssetGroup = (project: Project) =>
  maxBy(
    (project?.sections ?? []).flatMap((section) => unwrapEdges(section.assetGroups.edges)),
    (group) => DateTime.fromISO(group.createdAt).toMillis()
  );

export const getProjectLastUsedGenerationInfo = (project: Project) => {
  // A Blog Wizard run is named on its card only. Its requests carry no source type and no tool the
  // gallery offers.
  const newestAssetGroup = getProjectNewestAssetGroup(project);
  if (newestAssetGroup && isBlogWizardAssetGroup(newestAssetGroup)) {
    return {
      sourceType: GENERATION_SOURCE_TYPE.blogWizard,
      generationTool: null,
      customMultiPromptId: null,
      subTemplateId: null,
    };
  }

  const latestResult = getProjectLatestGenerationResult(project);
  if (!latestResult) {
    return null;
  }

  // take the relevant tool, keeping in mind that BE might also use "chain tool" to store it
  const latestResultTool = getGenerationRequestTool(latestResult);

  // make sure the tool is not deprecated
  const lastUsedTool = actualizeGenerationTool(latestResultTool);
  if (!isUsedTemplate(lastUsedTool)) {
    return null;
  }

  return {
    sourceType: latestResult.sourceType,
    generationTool: isFreestyleGenerationTool(lastUsedTool)
      ? GENERATION_TOOL.freestyle
      : lastUsedTool,
    customMultiPromptId: isSuggestGenerationResult(latestResult)
      ? latestResult.customMultiPromptId
      : null,
    subTemplateId: isSuggestGenerationResult(latestResult) ? latestResult.subTemplate?.id : null,
  };
};

export const isLongFormAssetGroup = (group: FeedAssetGroup): group is LongFormFeedGroup =>
  group.type === ASSET_GROUP_TYPE.longForm;

/** A translation's card: its rows are the original parts and their translations. */
export const isLocalizationAssetGroup = (group: Pick<FeedAssetGroup, 'sourceType'>): boolean =>
  group.sourceType === GENERATION_SOURCE_TYPE.localization;

/** A blog generated from the Blog Wizard panel. */
export const isBlogWizardAssetGroup = (group: Pick<FeedAssetGroup, 'sourceType'>): boolean =>
  group.sourceType === GENERATION_SOURCE_TYPE.blogWizard;

export const isShortFormAssetGroup = (group: FeedAssetGroup): group is ShortFormFeedGroup =>
  group.type === ASSET_GROUP_TYPE.shortForm;

export const isShortFormAsset = (asset: FeedAsset): asset is ShortFormFeedAsset => {
  const sfAsset = asset as ShortFormFeedAsset;
  return isSuggestionVariation(sfAsset) || isRecomposeVariation(sfAsset);
};

export const isLongFormAsset = (asset: FeedAsset): asset is LongFormFeedAsset =>
  !isShortFormAsset(asset);

/** The kind of card an asset sits in. It says which table holds the asset, and so which mutation scores it. */
export const getAssetGroupTypeOfAsset = (asset: FeedAsset): AssetGroupType =>
  isLongFormAsset(asset) ? ASSET_GROUP_TYPE.longForm : ASSET_GROUP_TYPE.shortForm;

/**
 * A document asset whose id is a long-form section's id. A saved-variations asset keeps its card's
 * kind in memory, but its id is a saved variation's.
 */
export const isLongFormDocumentAsset = (asset: {
  isSaved?: boolean;
  assetGroupType?: string;
}): boolean => !asset.isSaved && asset.assetGroupType === ASSET_GROUP_TYPE.longForm;
